/**
 * MUI ExportDocument Component
 * 
 * This component is the Material UI equivalent of the original ExportDocument component.
 * It renders a PDF document for export in the Room Designer application.
 * 
 * Note: Since @react-pdf/renderer is already a specialized library for PDF generation,
 * there's not much to change in terms of Material UI components. The MUI version
 * focuses on code organization and better documentation.
 */

import React from 'react';
import { Document, Page, StyleSheet, Font, View } from '@react-pdf/renderer';
import reportElements from '../sections';
import { get, isFunction } from 'lodash';

// Font declarations - paths will be resolved at runtime
// These ts-expect-error directives are necessary because the font files 
// don't have TypeScript definitions but are required for PDF rendering
// @ts-expect-error - Font imports will be properly typed in a future update
import GothamBook from '../../globalConstants/fonts/GothamBook.ttf';
// @ts-expect-error - Font imports will be properly typed in a future update
import GothamBold from '../../globalConstants/fonts/GothamBold.ttf';
// @ts-expect-error - Font imports will be properly typed in a future update
import GothamLight from '../../globalConstants/fonts/GothamLight.ttf';
// @ts-expect-error - Font imports will be properly typed in a future update
import GothamMedium from '../../globalConstants/fonts/GothamMedium.ttf';

// Register fonts for PDF generation
Font.register({
  family: "Gotham",
  fontStyle: "normal",
  fontWeight: "normal",
  fonts: [
    {
      src: GothamBook,
    },
    {
      src: GothamBold,
      fontWeight: "bold",
    },
    {
      src: GothamLight,
      fontWeight: "light",
    },
    {
      src: GothamMedium,
      fontWeight: "medium",
    },
  ],
});

// Define styles for the PDF document
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    lineHeight: 1.2,
    fontFamily: 'Gotham',
  },
});

// Define interfaces for the component props
interface Section {
  id: string;
  wrap?: boolean;
  pageBreakBefore?: boolean;
  content: SectionContent[];
}

interface SectionContent {
  element: string;
  label?: string | ((state: any) => string);
  content?: any;
  [key: string]: any;
}

interface ReportMetadata {
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  creator?: string;
  producer?: string;
}

// This interface creates a more permissive type to allow existing ReportDocument type
// to be used with this component without changing other files
interface ReportConfig {
  metadata: ReportMetadata;
  body: Section[];
  areas: (state: any) => Section[];
  [key: string]: any; // Allow additional properties for compatibility
}

interface ExportDocumentProps {
  // Make the report type more flexible to accommodate existing usage
  report: ReportConfig | any; // Allow any for backward compatibility
  store: {
    getState: () => any;
  };
}

interface ApplicationState {
  container: any;
  ui: {
    units: string;
  };
}

/**
 * Render a section of the PDF document
 * 
 * @param section - The section to render
 * @param state - The application state
 * @param sectionIndex - The index of the section
 * @returns The rendered section
 */
const renderSection = (
  section: Section,
  state: any,
  _sectionIndex: number
) => (
  <View key={`section-${section.id}`} wrap={section.wrap ?? false}>
    {section.content.map((child, index) => {
      const { element, label, content, ...rest } = child;
      const Element = reportElements[element];
      
      const props: any = {
        label: isFunction(label) ? label(state) : label,
        content: isFunction(content) ? content(state) : get(state, content) ?? content,
        ...rest
      };
      
      return props.content ? <Element key={`subline-${index}`} {...props} /> : null;
    })}
  </View>
);

/**
 * MUI ExportDocument component
 * 
 * @param props - Component props
 * @returns The MUI ExportDocument component
 */
const MuiExportDocument: React.FC<ExportDocumentProps> = ({ report, store }) => {
  const current: ApplicationState = store.getState();
  
  // Prepare state for the report
  const state = {
    ...current.container,
    // these are here for the table component only 
    isMeter: current.ui.units === 'meters', // this is required here for all dimensions
  };
  
  // Combine the report body with area-specific sections
  const areaSections = report.areas(state);
  const body = report.body.concat(...areaSections);

  // Split sections into pages - Equipment List starts on a new page
  const pages: Section[][] = [];
  let currentPage: Section[] = [];

  body.forEach((section: Section) => {
    const startsNewPage = (section.id === 'equipmentList' || section.id.endsWith('-equipmentList') || section.pageBreakBefore) && currentPage.length > 0;
    if (startsNewPage) {
      pages.push(currentPage);
      currentPage = [section];
    } else {
      currentPage.push(section);
    }
  });

  // Add the last page if it has content
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return (
    <Document {...report.metadata}>
      {pages.map((pageSections, pageIndex) => (
        <Page key={`page-${pageIndex}`} size="LETTER" style={styles.page}>
          {pageSections.map((section, idx) => renderSection(section, state, idx))}
        </Page>
      ))}
    </Document>
  );
};

export default MuiExportDocument;
